// Port of lib/json_store.py — the data layer.
//
// Python paths (pathlib.Path under PROJECT_ROOT/users) become logical storage paths
// ("users/<slug>.json") into the storage backend (src/lib/storage.ts). Python's atomic
// temp-file + os.replace write becomes a plain backend write — localStorage setItem is
// already atomic per key, so no .tmp dance is needed (see storage.ts header).

import {
  PlainDate,
  PlainTime,
  combine,
  compareDates,
  formatISODate,
  formatISOTime,
  nowISOSeconds,
  plainTime,
  tryParseISOTime,
} from "./datetime";
import { USERS_DIR } from "./config";
import { offset_hours_at, timezone_name_for, validate_offset_hours } from "./timezones";
import { getStorage } from "./storage";
import type { Candidate, Profile, RunData, TimesState } from "../types";

// ---------------------------------------------------------------------------
// Python-semantics helpers
// ---------------------------------------------------------------------------

function _is_plain_object(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Python truthiness: empty string/array/object, 0, null/undefined, false are falsy. */
function _truthy(value: any): boolean {
  if (value === null || value === undefined || value === false || value === 0 || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (_is_plain_object(value)) return Object.keys(value).length > 0;
  return Boolean(value);
}

/** Structural equality matching Python ==/!= on JSON-shaped values. */
function _deep_equal(a: any, b: any): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => _deep_equal(item, b[i]));
  }
  if (_is_plain_object(a) && _is_plain_object(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => k in b && _deep_equal(a[k], b[k]));
  }
  return false;
}

/** Python float() semantics: throws on "", non-numeric strings, and non-numbers. */
function _py_float(value: any): number {
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const s = value.trim();
    if (s === "") throw new Error(`could not convert string to float: '${value}'`);
    const n = Number(s);
    if (Number.isNaN(n)) throw new Error(`could not convert string to float: '${value}'`);
    return n;
  }
  throw new Error("float() argument must be a string or a real number");
}

/** Path.stem — final component without its last extension. */
function _path_stem(path: string): string {
  const name = path.slice(path.lastIndexOf("/") + 1);
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

/** Path.parent as a string prefix ("users/a.json" → "users"). */
function _path_parent(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash >= 0 ? path.slice(0, slash) : "";
}

function _path_exists(path: string): boolean {
  return getStorage().read(path) !== null;
}

// ---------------------------------------------------------------------------
// SearchSettings (Python frozen dataclass → interface + factory, per PORTING.md)
// ---------------------------------------------------------------------------

export interface SearchSettings {
  certainty: string;
  start: PlainTime;
  end: PlainTime;
  step_minutes: number;
  center_time: PlainTime | null;
  final_resolution_seconds: number;
  // Calendar dates for the window bounds. When end_date is the day after start_date
  // the window crosses midnight (overnight birth); each candidate second is then
  // mapped to the correct date during scoring.
  start_date: PlainDate | null;
  end_date: PlainDate | null;
}

export function make_search_settings(
  certainty: string,
  start: PlainTime,
  end: PlainTime,
  step_minutes: number,
  center_time: PlainTime | null = null,
  final_resolution_seconds = 1,
  start_date: PlainDate | null = null,
  end_date: PlainDate | null = null,
): SearchSettings {
  return { certainty, start, end, step_minutes, center_time, final_resolution_seconds, start_date, end_date };
}

/** SearchSettings.to_dict() */
export function search_settings_to_dict(settings: SearchSettings): Record<string, any> {
  return {
    certainty: settings.certainty,
    start: formatISOTime(settings.start),
    end: formatISOTime(settings.end),
    step_minutes: settings.step_minutes,
    coarse_step_minutes: settings.step_minutes,
    first_pass_spacing_fraction: 0.08,
    final_resolution_seconds: settings.final_resolution_seconds,
    precision_note:
      "step_minutes is only the coarse scan interval. The first pass spreads candidates by 8% of the searched span; later loops reduce spacing to 60 seconds and then 1 second. Candidate times are refined and saved at one-second resolution.",
    center_time: settings.center_time ? formatISOTime(settings.center_time) : null,
    start_date: settings.start_date ? formatISODate(settings.start_date) : null,
    end_date: settings.end_date ? formatISODate(settings.end_date) : null,
  };
}

// ---------------------------------------------------------------------------
// Slugs and paths
// ---------------------------------------------------------------------------

const _KEEP_CATEGORY_RE = /[\p{L}\p{N}\p{M}]/u; // L=letters, N=digits, M=combining marks

/**
 * File-safe, IDENTITY-PRESERVING slug. Keeps Unicode letters, digits AND combining
 * marks (stdlib \w misses the marks, mangling scripts like Devanagari: मोहन → म_हन), so
 * non-Latin names (मोहन, Müller, 京子) keep their identity instead of all collapsing to
 * "user" — which made every non-ASCII user silently share (and overwrite) one profile
 * file. Idempotent: sanitize(slug) == slug, so slugs can be passed back through safely.
 */
export function sanitize_user_name(name: string): string {
  const kept: string[] = [];
  for (const ch of String(name).trim().toLowerCase()) {
    // L=letters, N=digits, M=combining marks (vowel signs etc.); plus _ and -.
    kept.push(ch === "_" || ch === "-" || _KEEP_CATEGORY_RE.test(ch) ? ch : "_");
  }
  const normalized = kept.join("").replace(/_+/g, "_").replace(/^_+|_+$/g, "");
  return normalized || "user";
}

export function user_paths(first_name: string, users_dir: string = USERS_DIR): [string, string] {
  const slug = sanitize_user_name(first_name);
  return [`${users_dir}/${slug}.json`, `${users_dir}/${slug}_run.json`];
}

/**
 * Paths for a NEW person, guaranteed not to overwrite an existing profile: similar
 * names sanitize to the same slug ("John Smith" / "john-smith" / a second John), and
 * without this the second person silently destroys the first one's data. Returns
 * (user_path, run_path, slug) — store the returned slug on the profile and key every
 * later load off it.
 */
export function unique_user_paths(first_name: string, users_dir: string = USERS_DIR): [string, string, string] {
  const base = sanitize_user_name(first_name);
  let slug = base;
  let counter = 2;
  while (_path_exists(`${users_dir}/${slug}.json`) || _path_exists(`${users_dir}/${slug}_run.json`)) {
    slug = `${base}_${counter}`;
    counter += 1;
  }
  return [`${users_dir}/${slug}.json`, `${users_dir}/${slug}_run.json`, slug];
}

export function user_times_path(first_name_or_slug: string, users_dir: string = USERS_DIR): string {
  return `${users_dir}/${sanitize_user_name(first_name_or_slug)}_times.json`;
}

export function times_path_from_user_path(user_path: string): string {
  return user_times_path(_path_stem(user_path), _path_parent(user_path));
}

export function user_draft_paths(users_dir: string = USERS_DIR): [string, string] {
  return [`${users_dir}/_draft.json`, `${users_dir}/_draft_run.json`];
}

export function user_log_path(first_name_or_slug: string, users_dir: string = USERS_DIR): string {
  return `${users_dir}/${sanitize_user_name(first_name_or_slug)}.log`;
}

export function aggregate_user_log_path(users_dir: string = USERS_DIR): string {
  return `${users_dir}/user.log`;
}

/**
 * Disabled. Verbose per-candidate/OpenAI logging produced multi-GB .log files;
 * it is intentionally a no-op. Kept as a stub so existing call sites stay valid.
 */
export function append_verbose_log(_user_or_slug: Record<string, any> | string, _event: string, _payload: any): void {
  return;
}

// ---------------------------------------------------------------------------
// JSON read/write
// ---------------------------------------------------------------------------

export function read_json(path: string, default_: Record<string, any> | null = null): Record<string, any> {
  const raw = getStorage().read(path);
  if (raw === null) {
    return default_ !== null ? { ...default_ } : {};
  }
  try {
    return JSON.parse(raw);
  } catch (exc) {
    throw new Error(`Profile file ${_path_stem(path)}.json is corrupt and cannot be loaded: ${exc}`);
  }
}

/**
 * Persist a payload. Python wrote atomically (temp file + os.replace) so a crash could not
 * tear a profile; localStorage writes are already atomic per key, so this is a plain write.
 */
export function write_json(path: string, payload: Record<string, any>): void {
  getStorage().write(path, JSON.stringify(payload, null, 2));
}

// ---------------------------------------------------------------------------
// Events normalization
// ---------------------------------------------------------------------------

export function empty_matching_instructions(): Record<string, any> {
  return {
    internal_metric_targets: [],
    confidence: 0.0,
  };
}

export function normalize_matching_instructions(value: any): Record<string, any> {
  const normalized = empty_matching_instructions();
  if (_is_plain_object(value)) {
    const targets: Record<string, any>[] = [];
    for (const target of value["internal_metric_targets"] || []) {
      if (!_is_plain_object(target)) continue;
      const metric = String(target["metric"] ?? "").trim();
      const expected_direction = String(target["expected_direction"] ?? "").trim();
      if (metric && expected_direction) {
        targets.push({ metric, expected_direction });
      }
    }
    normalized["internal_metric_targets"] = targets;
    try {
      const raw = "confidence" in value ? value["confidence"] : normalized["confidence"];
      normalized["confidence"] = _py_float(_truthy(raw) ? raw : 0.0);
    } catch {
      normalized["confidence"] = 0.0;
    }
  }
  return normalized;
}

const _EVENT_DROP_KEYS = new Set(["source_question_id", "source", "question_id", "notes", "matching_instructions", "raw_match_expectations"]);

export function normalize_event(event: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {};
  for (const [key, value] of Object.entries(event)) {
    if (!_EVENT_DROP_KEYS.has(key)) cleaned[key] = value;
  }
  return cleaned;
}

export function normalized_text(value: any): string {
  return String(_truthy(value) ? value : "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
}

// ---------------------------------------------------------------------------
// Candidate times
// ---------------------------------------------------------------------------

export function compact_candidate(candidate: Record<string, any>): Record<string, any> {
  const keep = [
    "deterministic_score",
    "event_score",
    "ai_score",
    "final_score",
    "assessment",
    "ai_summary",
    "personality_paragraph",
    "milestone_periods",
    "event_fact_match",
    "other_notable_things",
    "evidence_for",
    "evidence_against",
    "rejected",
    "generation_method",
    "search_pass",
    "coarse_seed_time",
  ];
  const out: Record<string, any> = {};
  for (const key of keep) {
    if (!(key in candidate)) continue;
    const value = candidate[key];
    // Python: `not in (None, "", [], {})` — drop null/empty-string/empty-list/empty-dict.
    const empty =
      value === null ||
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      (_is_plain_object(value) && Object.keys(value).length === 0);
    if (!empty) out[key] = value;
  }
  return out;
}

export function full_times_dict_from_candidates(candidates: Candidate[]): Record<string, Record<string, any>> {
  const times: Record<string, Record<string, any>> = {};
  for (const candidate of candidates) {
    const time_key = String(_truthy(candidate["time"]) ? candidate["time"] : "").trim();
    if (!time_key) continue;
    const payload: Record<string, any> = {};
    for (const [key, value] of Object.entries(candidate)) {
      if (key !== "time") payload[key] = value;
    }
    times[time_key] = payload;
  }
  return times;
}

export function profile_times_dict_from_candidates(candidates: Candidate[]): Record<string, Record<string, any>> {
  const times: Record<string, Record<string, any>> = {};
  for (const candidate of candidates) {
    const time_key = String(_truthy(candidate["time"]) ? candidate["time"] : "").trim();
    if (!time_key) continue;
    const payload: Record<string, any> = {};
    if (candidate["deterministic_score"] !== null && candidate["deterministic_score"] !== undefined) {
      payload["calculated_score"] = candidate["deterministic_score"];
    } else if (candidate["calculated_score"] !== null && candidate["calculated_score"] !== undefined) {
      payload["calculated_score"] = candidate["calculated_score"];
    }
    if (candidate["ai_score"] !== null && candidate["ai_score"] !== undefined) {
      payload["ai_score"] = candidate["ai_score"];
    }
    if (_truthy(candidate["rejected"])) {
      payload["rejected"] = true;
    }
    times[time_key] = payload;
  }
  return times;
}

export function build_times_state(user_profile: Profile, candidates: Candidate[]): TimesState {
  return {
    schema_version: 1,
    user_slug: user_profile["slug"] ?? "user",
    updated_at: nowISOSeconds(),
    times: full_times_dict_from_candidates(candidates),
  };
}

export function candidate_sort_score(candidate: Record<string, any>): number {
  for (const key of ["deterministic_score", "calculated_score", "raw_event_score", "event_score", "final_score", "ai_score"]) {
    if (candidate[key] !== null && candidate[key] !== undefined) {
      return _py_float(candidate[key]);
    }
  }
  return -Infinity;
}

export function candidate_list_from_times(
  user_profile: Profile,
  include_rejected = false,
  limit: number | null = null,
): Candidate[] {
  const times_state = _truthy(user_profile["_times"]) ? user_profile["_times"]! : {};
  const times: Record<string, any> = _truthy((times_state as any)["times"])
    ? (times_state as any)["times"]
    : _truthy(user_profile["times"])
      ? user_profile["times"]!
      : {};
  const candidates: Candidate[] = [];
  for (const [time_key, payload] of Object.entries(times)) {
    const candidate: Candidate = { time: time_key, ...(payload || {}) };
    if (!("deterministic_score" in candidate) && "calculated_score" in candidate) {
      candidate["deterministic_score"] = candidate["calculated_score"];
    }
    if (include_rejected || !_truthy(candidate["rejected"])) {
      candidates.push(candidate);
    }
  }
  // Stable descending sort, like Python sorted(key=..., reverse=True).
  candidates.sort((a, b) => {
    const sa = candidate_sort_score(a);
    const sb = candidate_sort_score(b);
    if (sa === sb) return 0;
    return sb > sa ? 1 : -1;
  });
  return limit !== null ? candidates.slice(0, limit) : candidates;
}

export function store_candidate_times(user_profile: Profile, candidates: Candidate[]): void {
  user_profile["times"] = profile_times_dict_from_candidates(candidates);
  user_profile["_times"] = build_times_state(user_profile, candidates);
  delete user_profile["candidate_times"];
  delete user_profile["rejected_times"];
}

// ---------------------------------------------------------------------------
// Save pipeline
// ---------------------------------------------------------------------------

export function clean_user_profile_for_save(user_profile: Profile): Record<string, any> {
  const cleaned: Record<string, any> = { ...user_profile };
  delete cleaned["_times"];
  if ("candidate_times" in cleaned && !("times" in cleaned)) {
    cleaned["times"] = profile_times_dict_from_candidates(cleaned["candidate_times"] ?? []);
  }
  delete cleaned["candidate_times"];
  delete cleaned["rejected_times"];
  delete cleaned["question_history"];
  cleaned["events"] = (cleaned["events"] ?? []).filter(_is_plain_object).map(normalize_event);
  delete cleaned["facts"];
  delete cleaned["info"];
  // Candidate scores are EPHEMERAL: never duplicated into the saved base profile. They live
  // only in the local times file (for in-session reloads) and are recomputed on open — so the
  // base item carries an empty map. Kept as a present key for the save-shape contract.
  cleaned["times"] = {};
  return cleaned;
}

export function clean_times_state_for_save(user_profile: Profile): TimesState {
  const existing: TimesState = _truthy(user_profile["_times"]) ? user_profile["_times"]! : {};
  if (_truthy(existing["times"])) {
    return {
      schema_version: existing["schema_version"] ?? 1,
      user_slug: user_profile["slug"] ?? existing["user_slug"] ?? "user",
      updated_at: nowISOSeconds(),
      times: existing["times"] ?? {},
    };
  }
  return build_times_state(user_profile, candidate_list_from_times(user_profile, true));
}

export function validate_user_profile_for_save(user_profile: Record<string, any>): void {
  const birth = user_profile["birth"];
  if (!_is_plain_object(birth)) {
    throw new Error("User profile cannot be saved without a birth object.");
  }
  const missing_birth = ["date", "lat", "lon", "timezone_offset"].filter(
    (key) => !(key in birth) || birth[key] === null || birth[key] === undefined || birth[key] === "",
  );
  if (missing_birth.length) {
    throw new Error(`User profile cannot be saved without birth.${missing_birth.join(", birth.")}.`);
  }
  for (const key of ["events", "times"]) {
    if (!(key in user_profile)) {
      throw new Error(`User profile cannot be saved without durable \`${key}\`.`);
    }
  }
}

export function clean_run_data_for_save(run_data: RunData): Record<string, any> {
  const allowed = new Set([
    "schema_version",
    "user_slug",
    "created_at",
    "updated_at",
    "search",
    "stage",
    // NOTE: `current_questions` is deliberately NOT persisted — it is regenerated on load
    // (ensure_questions / load_or_create_run), so saving it only risks restoring stale prompts.
    "ai_cost_total",
  ]);
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(run_data)) {
    if (allowed.has(key)) out[key] = value;
  }
  return out;
}

export function save_state(user_path: string, run_path: string, user_profile: Profile, run_data: RunData): void {
  const cleaned_user = clean_user_profile_for_save(user_profile);
  validate_user_profile_for_save(cleaned_user);
  write_json(user_path, cleaned_user);
  write_json(run_path, clean_run_data_for_save(run_data));
  write_json(times_path_from_user_path(user_path), clean_times_state_for_save(user_profile));
}

export function append_log(run_data: RunData, message: string): void {
  const line = { at: nowISOSeconds(), message };
  if (!Array.isArray(run_data["_console"])) run_data["_console"] = [];
  run_data["_console"]!.push(line);
  if (_truthy(run_data["user_slug"])) {
    append_verbose_log(run_data["user_slug"]!, "console", line);
  }
}

// ---------------------------------------------------------------------------
// Profile construction
// ---------------------------------------------------------------------------

/**
 * The profile's DEFAULT offset: the zone at the birth coordinates, sampled at noon of
 * the birth date (historical DST rules apply via the tzdb). Scoring re-resolves the offset
 * per candidate moment and per event (lib/timezones), so this stored value is the
 * fallback, not the only truth. Raises with a user-actionable message for coordinates in
 * no zone (open ocean) rather than silently charting in UTC.
 */
export function resolve_timezone_offset(lat: number, lon: number, birth_date: PlainDate): [number, string] {
  const timezone_name = timezone_name_for(lat, lon);
  if (!timezone_name) {
    throw new Error(
      "Could not resolve a timezone from these coordinates — they may be in the " +
        "ocean or swapped (latitude first, then longitude). Check the birth place.",
    );
  }
  const offset = offset_hours_at(timezone_name, combine(birth_date, plainTime(12, 0)));
  if (offset === null) {
    throw new Error(`Could not determine UTC offset for timezone ${timezone_name}.`);
  }
  return [validate_offset_hours(offset), timezone_name];
}

export function build_user_profile(
  first_name: string,
  birth_date: PlainDate,
  lat: number,
  lon: number,
  certainty: string,
  recorded_time: PlainTime | null,
  range_start: PlainTime | null,
  range_end: PlainTime | null,
  range_end_date: PlainDate | null = null,
  slug: string | null = null,
  tz_offset_override: number | null = null,
): Profile {
  const [resolved_offset, timezone_name] = resolve_timezone_offset(lat, lon, birth_date);
  // A user-confirmed offset (intake surfaces this for pre-1970 births, where the IANA tzdb's
  // DST projection is unreliable) is authoritative and PINNED: stored as the offset plus a
  // `timezone_offset_fixed` flag that tells `birth_offset_at` to skip the tzdb guess entirely.
  const has_override = tz_offset_override !== null && Number.isFinite(tz_offset_override);
  const timezone_offset = has_override ? validate_offset_hours(Number(tz_offset_override)) : resolved_offset;
  // The window starts on the birth date; an overnight birth ends on range_end_date.
  const endDate = range_end_date || birth_date;
  const search = build_search_settings(certainty, recorded_time, range_start, range_end, birth_date, endDate);
  return {
    first_name: first_name.trim(),
    // `slug` is the storage identity (collision-disambiguated by unique_user_paths);
    // derive it only as a fallback for callers that didn't pre-reserve one.
    slug: slug || sanitize_user_name(first_name),
    birth: {
      date: formatISODate(birth_date),
      lat,
      lon,
      timezone: timezone_name,
      timezone_offset,
      timezone_offset_fixed: has_override,
      time_certainty: certainty,
      entered_time: recorded_time ? formatISOTime(recorded_time) : null,
      range_start: range_start ? formatISOTime(range_start) : null,
      range_end: range_end ? formatISOTime(range_end) : null,
      // Persisted so a full reset (full_reset_keep_birth_events) can restore an overnight /
      // multi-day window's true end date instead of collapsing it back onto the birth date.
      ...(range_end_date ? { range_end_date: formatISODate(range_end_date) } : {}),
    },
    search: search_settings_to_dict(search),
    events: [],
    times: {},
    loop_count: 0,
    updated_at: nowISOSeconds(),
  };
}

// ---------------------------------------------------------------------------
// Time helpers and search-window construction
// ---------------------------------------------------------------------------

export function time_to_minutes(value: PlainTime): number {
  return value.hour * 60 + value.minute;
}

export function minutes_to_time(value: number): PlainTime {
  const v = ((value % (24 * 60)) + 24 * 60) % (24 * 60);
  return plainTime(Math.floor(v / 60), v % 60);
}

export function clamped_minutes_to_time(value: number): PlainTime {
  const v = Math.max(0, Math.min(24 * 60 - 1, value));
  return plainTime(Math.floor(v / 60), v % 60);
}

export function parse_hms(value: string | null | undefined): PlainTime | null {
  if (!value) return null;
  return tryParseISOTime(value);
}

export function circular_range_width_minutes(start: PlainTime, end: PlainTime): number {
  const start_minutes = time_to_minutes(start);
  let end_minutes = time_to_minutes(end);
  if (end_minutes < start_minutes) {
    end_minutes += 24 * 60;
  }
  return Math.max(1, end_minutes - start_minutes);
}

export function build_search_settings(
  certainty: string,
  entered_time: PlainTime | null,
  range_start: PlainTime | null,
  range_end: PlainTime | null,
  start_date: PlainDate | null = null,
  end_date: PlainDate | null = null,
): SearchSettings {
  if (certainty === "recorded") {
    if (entered_time === null) throw new Error("Recorded birth time is required.");
    const center = time_to_minutes(entered_time);
    return make_search_settings(certainty, clamped_minutes_to_time(center - 120), clamped_minutes_to_time(center + 120), 3, entered_time);
  }
  if (certainty === "approximate") {
    if (entered_time === null) throw new Error("Approximate birth time is required.");
    const center = time_to_minutes(entered_time);
    return make_search_settings(certainty, clamped_minutes_to_time(center - 360), clamped_minutes_to_time(center + 360), 5, entered_time);
  }
  if (certainty === "range") {
    if (range_start === null || range_end === null) {
      throw new Error("Start and end times are required for range mode.");
    }
    // The window can span one day (same dates), a single overnight (end_date = start_date + 1), or
    // MULTIPLE days (end_date ≥ start_date + 1, e.g. an induced-delivery search where the natural
    // birth may have been a day or two before the scheduled time). Width is the true calendar gap.
    const day_gap = start_date && end_date ? compareDates(end_date, start_date) : 0;
    const multiday = day_gap > 0;
    const start_minutes = time_to_minutes(range_start);
    let end_minutes = time_to_minutes(range_end);
    if (!multiday && end_minutes <= start_minutes) {
      throw new Error("Range end must be later than range start on the same birth date (set a later end date for an overnight or multi-day birth).");
    }
    const width = day_gap * 24 * 60 + (end_minutes - start_minutes);
    if (width <= 0) {
      throw new Error("Range end must be later than range start.");
    }
    return make_search_settings(
      certainty,
      range_start,
      range_end,
      Math.max(10, Math.trunc(Math.ceil(width / 20))),
      null,
      1,
      start_date,
      end_date,
    );
  }
  if (certainty === "unknown") {
    return make_search_settings(certainty, plainTime(0, 0), plainTime(23, 59), 60);
  }
  throw new Error(`Unknown birth time certainty: ${certainty}`);
}

/**
 * Replace the profile's search window AFTER creation (the intake mis-entry fix — e.g. a window
 * entered as 02:00→14:00 when the birth was actually 14:00→02:00 overnight). Rebuilds the search
 * as an explicit RANGE via `build_search_settings` (same-day, overnight via end_date = birth+1, or
 * multi-day), so the window math is identical to intake. The certainty becomes "range" — an edited
 * window searches ranking-neutral (the recorded-time prior belongs to a trusted entered clock, and
 * dropping the recorded certainty also keeps `normalize_same_day_search_window` from "correcting"
 * the edit back on the next load). Resets everything derived from the old window: the sign lock,
 * zoom history, base window, refinement loop, and stamps stored candidate scores stale so nothing
 * computed against the wrong window survives. Throws (from build_search_settings) on an invalid
 * window; the profile is only mutated after the new settings built successfully.
 */
export function replace_search_window(
  user_profile: Profile,
  range_start: PlainTime,
  range_end: PlainTime,
  start_date: PlainDate,
  end_date: PlainDate,
  run_data: RunData | null = null,
): void {
  const settings = build_search_settings("range", null, range_start, range_end, start_date, end_date);
  const dict = search_settings_to_dict(settings);
  user_profile["search"] = dict;
  // PERMANENT correction: the birth range fields are the canonical baseline every "restore the
  // original window" path reads (zoom-out's empty-stack fallback, full reset) — update them too,
  // or those paths would resurrect the mis-entered window.
  if (!_is_plain_object(user_profile["birth"])) user_profile["birth"] = {};
  user_profile["birth"]!["range_start"] = formatISOTime(settings.start);
  user_profile["birth"]!["range_end"] = formatISOTime(settings.end);
  user_profile["birth"]!["range_end_date"] =
    compareDates(end_date, start_date) !== 0 ? formatISODate(end_date) : null;
  // The correction is PERMANENT: the caller persists the profile (base) — and the run record
  // carries its own queryable `search` copy, so keep it in lockstep (same as the normaliser).
  if (run_data !== null) {
    run_data["search"] = dict;
    append_log(
      run_data,
      `Search window corrected to ${formatISOTime(settings.start)}–${formatISOTime(settings.end)} (${formatISODate(start_date)} → ${formatISODate(end_date)}).`,
    );
  }
  user_profile["rising_sign_locked"] = false;
  user_profile["zoom_stack"] = [];
  user_profile["base_window"] = null;
  user_profile["loop_count"] = 0;
  for (const item of user_profile["candidate_times"] ?? []) {
    (item as Record<string, any>)["generation_method"] = "stale_search_window";
  }
  for (const item of Object.values(user_profile["times"] ?? {})) {
    (item as Record<string, any>)["generation_method"] = "stale_search_window";
  }
  for (const item of Object.values((user_profile["_times"] ?? {})["times"] ?? {})) {
    (item as Record<string, any>)["generation_method"] = "stale_search_window";
  }
  append_verbose_log(user_profile as Record<string, any>, "search_window.edited", {
    start: formatISOTime(settings.start),
    end: formatISOTime(settings.end),
    start_date: formatISODate(start_date),
    end_date: formatISODate(end_date),
  });
}

export function normalize_same_day_search_window(user_profile: Profile, run_data: RunData | null = null): boolean {
  const search: Record<string, any> = _truthy(user_profile["search"]) ? user_profile["search"]! : {};
  const certainty = search["certainty"];
  if (certainty !== "recorded" && certainty !== "approximate") {
    return false;
  }

  const center = parse_hms(search["center_time"]) || parse_hms((user_profile["birth"] ?? {})["entered_time"]);
  if (center === null) {
    return false;
  }

  const expected = search_settings_to_dict(build_search_settings(certainty, center, null, null));
  const current: Record<string, any> = {
    certainty: search["certainty"] ?? null,
    start: search["start"] ?? null,
    end: search["end"] ?? null,
    step_minutes: search["step_minutes"] ?? null,
    coarse_step_minutes: search["coarse_step_minutes"] ?? null,
    first_pass_spacing_fraction: search["first_pass_spacing_fraction"] ?? null,
    final_resolution_seconds: search["final_resolution_seconds"] ?? null,
    precision_note: search["precision_note"] ?? null,
    center_time: search["center_time"] ?? null,
  };
  // This normaliser exists to REPAIR windows that WRAP off the birth date (its own log text says
  // exactly that) — it must NOT undo a legitimately NARROWED window (a committed rising sign or a
  // drill-down zoom saved inside the default ±2h/±6h band). So the reset fires only when the
  // current window is NOT contained within the freshly built default: contained-and-narrower
  // windows are the user's progress and are left untouched. Containment is measured FORWARD from
  // the default's start in seconds-of-day, which also handles a midnight-crossing default.
  const cur_start = parse_hms(search["start"]);
  const cur_end = parse_hms(search["end"]);
  if (cur_start !== null && cur_end !== null) {
    const DAY_SECONDS = 24 * 3600;
    const secs = (t: PlainTime) => t.hour * 3600 + t.minute * 60 + (t.second ?? 0);
    const exp_start = secs(parse_hms(expected["start"])!);
    let exp_width = secs(parse_hms(expected["end"])!) - exp_start;
    if (exp_width < 0) exp_width += DAY_SECONDS; // overnight/midnight-crossing default
    const start_offset = (((secs(cur_start) - exp_start) % DAY_SECONDS) + DAY_SECONDS) % DAY_SECONDS;
    let cur_width = secs(cur_end) - secs(cur_start);
    if (cur_width < 0) cur_width += DAY_SECONDS; // current window crossing midnight
    if (start_offset + cur_width <= exp_width) {
      return false; // contained in the expected default — nothing wraps, nothing to repair
    }
  }

  user_profile["search"] = expected;
  if (!_is_plain_object(user_profile["birth"])) user_profile["birth"] = {};
  user_profile["birth"]!["entered_time"] = formatISOTime(center);
  if (run_data !== null) {
    run_data["search"] = expected;
    if (_truthy(user_profile["candidate_times"])) {
      for (const item of user_profile["candidate_times"]) {
        item["generation_method"] = "stale_search_window";
      }
    }
    if (_truthy(user_profile["times"])) {
      for (const item of Object.values(user_profile["times"]!)) {
        (item as Record<string, any>)["generation_method"] = "stale_search_window";
      }
    }
    if (_truthy((user_profile["_times"] ?? {})["times"])) {
      for (const item of Object.values(user_profile["_times"]!["times"]!)) {
        (item as Record<string, any>)["generation_method"] = "stale_search_window";
      }
    }
    append_log(run_data, `Corrected ${certainty} search window to stay within the birth date: ${expected["start"]} to ${expected["end"]}.`);
  }
  append_verbose_log(user_profile as Record<string, any>, "search_window.corrected_same_day", {
    reason: "Recorded/approximate birth-time windows must clamp to the birth date and must not wrap to the previous or next day.",
    previous: current,
    corrected: expected,
  });
  return true;
}

export function empty_run_data(user_profile: Profile): RunData {
  return {
    schema_version: 1,
    user_slug: user_profile["slug"],
    created_at: nowISOSeconds(),
    updated_at: nowISOSeconds(),
    search: user_profile["search"],
    stage: "answering_questions",
    current_questions: [],
  };
}

export function ensure_user_profile_sections(user_profile: Profile, run_data: RunData | null = null): boolean {
  let changed = false;
  if ("candidate_times" in user_profile && !("times" in user_profile)) {
    const candidates = user_profile["candidate_times"] ?? [];
    user_profile["times"] = profile_times_dict_from_candidates(candidates);
    user_profile["_times"] = build_times_state(user_profile, candidates);
    delete user_profile["candidate_times"];
    changed = true;
  }
  if ("rejected_times" in user_profile) {
    for (const time_label of user_profile["rejected_times"] ?? []) {
      if (!_is_plain_object(user_profile["times"])) user_profile["times"] = {};
      if (!_is_plain_object(user_profile["times"]![time_label])) user_profile["times"]![time_label] = {};
      user_profile["times"]![time_label]["rejected"] = true;
    }
    delete user_profile["rejected_times"];
    changed = true;
  }
  if ("question_history" in user_profile) {
    delete user_profile["question_history"];
    changed = true;
  }
  // The facts feature has been removed: any legacy `facts`/`info` carried on an old profile
  // is dropped on load rather than migrated.
  if ("facts" in user_profile) {
    delete user_profile["facts"];
    changed = true;
  }
  if ("events" in user_profile) {
    const compacted_events = (user_profile["events"] ?? []).filter(_is_plain_object).map(normalize_event);
    if (!_deep_equal(compacted_events, user_profile["events"] ?? [])) {
      user_profile["events"] = compacted_events;
      changed = true;
    }
  }
  if ("info" in user_profile) {
    delete user_profile["info"];
    changed = true;
  }
  if (normalize_same_day_search_window(user_profile, run_data)) {
    changed = true;
  }
  const search: Record<string, any> = _truthy(user_profile["search"]) ? user_profile["search"]! : {};
  if (_truthy(search)) {
    const step_minutes = Math.trunc(
      _truthy(search["step_minutes"]) ? search["step_minutes"] : _truthy(search["coarse_step_minutes"]) ? search["coarse_step_minutes"] : 1,
    );
    const precision_updates: Record<string, any> = {
      coarse_step_minutes: step_minutes,
      first_pass_spacing_fraction: 0.08,
      final_resolution_seconds: 1,
      precision_note:
        "step_minutes is only the coarse scan interval. The first pass spreads candidates by 8% of the searched span; later loops reduce spacing to 60 seconds and then 1 second. Candidate times are refined and saved at one-second resolution.",
    };
    if ("output_bucket_minutes" in search) {
      delete search["output_bucket_minutes"];
      changed = true;
    }
    for (const [key, value] of Object.entries(precision_updates)) {
      if (!_deep_equal(search[key], value)) {
        search[key] = value;
        changed = true;
      }
    }
  }
  if (run_data !== null) {
    for (const key of ["suggestions", "events"]) {
      const current = user_profile[key];
      const should_migrate =
        key in run_data &&
        (current === null ||
          current === undefined ||
          (_is_plain_object(current) && Object.keys(current).length === 0) ||
          (Array.isArray(current) && current.length === 0) ||
          (key === "suggestions" && !Object.values(_truthy(current) ? current : {}).some(_truthy)));
      if (should_migrate) {
        const migrated = run_data[key];
        delete run_data[key];
        if (key === "suggestions") {
          if (!_truthy(run_data["current_questions"])) {
            run_data["current_questions"] = questions_from_legacy_suggestions(migrated);
          }
          run_data["stage"] = "answering_questions";
        } else {
          user_profile[key] = migrated;
        }
        changed = true;
      }
    }
    // The facts feature has been removed: legacy `info`/`checkbox_answers` on run data are
    // dropped (previously they migrated into profile facts).
    for (const stale_key of ["info", "checkbox_answers"]) {
      if (stale_key in run_data) {
        delete run_data[stale_key];
        changed = true;
      }
    }
    const top_candidates: Record<string, any>[] = run_data["top_candidates"] ?? [];
    const seed_candidates_only = top_candidates.filter(
      (item) =>
        (item["deterministic_score"] === null || item["deterministic_score"] === undefined) &&
        (item["ai_score"] === null || item["ai_score"] === undefined) &&
        (item["final_score"] === null || item["final_score"] === undefined),
    );
    if (seed_candidates_only.length && seed_candidates_only.length === top_candidates.length) {
      run_data["top_candidates"] = [];
      changed = true;
    }
  }

  const profile_defaults: Record<string, any> = { events: [], times: {} };
  for (const [key, default_] of Object.entries(profile_defaults)) {
    if (!(key in user_profile)) {
      user_profile[key] = Array.isArray(default_) ? [...default_] : { ...default_ };
      changed = true;
    }
  }
  if (!("loop_count" in user_profile)) {
    user_profile["loop_count"] = 0;
    changed = true;
  }

  if (run_data !== null) {
    const run_defaults: Record<string, any> = { current_questions: [] };
    for (const [key, default_] of Object.entries(run_defaults)) {
      if (!(key in run_data)) {
        run_data[key] = Array.isArray(default_) ? [...default_] : default_;
        changed = true;
      }
    }
    for (const stale_key of ["draft_answers", "extra_info", "visible_candidate_times", "last_ai_review", "console", "openai"]) {
      if (stale_key in run_data) {
        delete run_data[stale_key];
        changed = true;
      }
    }
    if (run_data["stage"] === "awaiting_input") {
      run_data["stage"] = _truthy(run_data["current_questions"]) ? "answering_questions" : "ready_for_questions";
      changed = true;
    }
  }

  return changed;
}

export function questions_from_legacy_suggestions(suggestions: Record<string, any>): Record<string, any>[] {
  const questions: Record<string, any>[] = [];
  for (const section of ["info", "events", "checkboxes"]) {
    for (const item of (suggestions[section] ?? []).slice(0, 5)) {
      const label = item["label"] ?? "";
      const why = item["why"] ?? "";
      const text = _truthy(why) ? `${label}: ${why}` : label;
      if (_truthy(text)) {
        questions.push({ id: `legacy_${questions.length + 1}`, question: text, suggestions: [text], answer_kind: "mixed" });
      }
      if (questions.length >= 5) {
        return questions;
      }
    }
  }
  return questions;
}

export function load_or_create_run(user_profile: Profile, run_path: string): RunData {
  if (_path_exists(run_path)) {
    return read_json(run_path);
  }
  const run_data = empty_run_data(user_profile);
  append_log(run_data, "Created a new rectification run.");
  write_json(run_path, run_data);
  return run_data;
}

export function load_times_state(user_profile: Profile, times_path: string): TimesState {
  if (_path_exists(times_path)) {
    const times_state = read_json(times_path);
    if ("times" in times_state) {
      return times_state;
    }
  }
  return build_times_state(user_profile, candidate_list_from_times(user_profile, true));
}
